/**
 * Admin Routing Lab
 * 
 * SIMPLIFIED: Create a single live lead and see the outcome.
 * This page is a microscope, not a dashboard.
 * 
 * Responsibilities:
 * - Create a single live lead
 * - Trigger routing via intakeLead()
 * - Display: Lead ID, Final status, Link to Inspector
 * 
 * Removed: System state, retry worker, pool selection, simulation presets, metrics
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAllocationWeights } from "@/contexts/AllocationWeightsContext";
import { usePricingRules } from "@/contexts/PricingRulesContext";
import { useSystemControl } from "@/contexts/SystemControlContext";
import { useMockAgents } from "@/contexts/MockAgentsContext";
import { useContracts } from "@/contexts/ContractsContext";
import { intakeLead, createTestLeadPayload, type IntakeResult } from "@/lib/leads";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Beaker, 
  Play, 
  CheckCircle2,
  XCircle,
  RefreshCw,
  ArrowRight,
  Eye,
  Loader2,
  Package,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { US_STATES, LEAD_TYPE_OPTIONS } from "@/lib/simulateIncomingLead";

export default function AdminRoutingLab() {
  const { actions: weightsActions } = useAllocationWeights();
  const { actions: pricingActions } = usePricingRules();
  const { actions: systemActions } = useSystemControl();
  const { clientAgents } = useMockAgents();
  const { actions: contractActions } = useContracts();
  const { toast } = useToast();
  
  // Form state
  const [name, setName] = useState("John Doe");
  const [phone, setPhone] = useState("555-123-4567");
  const [selectedState, setSelectedState] = useState("TX");
  const [selectedLeadType, setSelectedLeadType] = useState("Final Expense");
  
  // Result state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<IntakeResult | null>(null);
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setResult(null);
    
    // Build routing context
    const allocationWeights = weightsActions.getWeights();
    const contractPressures = contractActions.getAllPressures();
    
    const routingContext = {
      agents: clientAgents,
      weights: allocationWeights,
      getPricingRule: (leadTypeId: string, state: string) => {
        const rule = pricingActions.getPricingRuleForLead(leadTypeId, state);
        if (rule) {
          return { basePrice: rule.basePrice, active: rule.active };
        }
        return null;
      },
      checkIntake: (vertical: "life_insurance" | "mortgage") => {
        return systemActions.getIntakeRejectionReason(vertical);
      },
      contractPressures,
      targetPoolSize: 8,
      usePoolSelection: true,
    };
    
    // Small delay for UX
    await new Promise(resolve => setTimeout(resolve, 200));
    
    try {
      const intakeResult = intakeLead(
        createTestLeadPayload({
          name,
          phone,
          state: selectedState,
          leadType: selectedLeadType,
          source: 'admin_routing_lab',
        }),
        {
          routingContext,
          debug: false,
          onLeadPersisted: (lead) => {
            console.log('[RoutingLab] Lead persisted:', lead.id);
          },
          onRoutingComplete: (routingResult) => {
            if (routingResult.success && routingResult.deliveryImpact.contractId) {
              contractActions.recordDelivery(routingResult.deliveryImpact.contractId);
            }
          },
        }
      );
      
      setResult(intakeResult);
      
      if (intakeResult.success && intakeResult.routingOutcome === 'ROUTED') {
        toast({
          title: "Lead Routed",
          description: `Assigned to ${intakeResult.assignedAgentName} for $${intakeResult.chargedAmount}`,
        });
      } else if (intakeResult.routingOutcome === 'RETRY_QUEUED') {
        toast({
          title: "Lead Queued for Retry",
          description: `Will retry (attempt ${intakeResult.retryCount + 1})`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Lead Unsold",
          description: intakeResult.intakeStatus,
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process lead",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleReset = () => {
    setResult(null);
    setName("John Doe");
    setPhone("555-123-4567");
    setSelectedState("TX");
    setSelectedLeadType("Final Expense");
  };
  
  const getOutcomeIcon = (outcome: string | undefined) => {
    switch (outcome) {
      case "ROUTED": return <CheckCircle2 className="h-6 w-6 text-green-600" />;
      case "RETRY_QUEUED": return <RefreshCw className="h-6 w-6 text-blue-600" />;
      case "STOCKED": return <Package className="h-6 w-6 text-muted-foreground" />;
      default: return <XCircle className="h-6 w-6 text-destructive" />;
    }
  };
  
  const getOutcomeColor = (outcome: string | undefined) => {
    switch (outcome) {
      case "ROUTED": return "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800";
      case "RETRY_QUEUED": return "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800";
      case "STOCKED": return "bg-muted/50 border-muted";
      default: return "bg-destructive/10 border-destructive/20";
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Beaker className="h-6 w-6" />
            Routing Lab
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create a single test lead and observe the routing outcome
          </p>
        </div>

        {/* Lead Input Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Create Test Lead</CardTitle>
            <CardDescription>
              This will trigger live routing through the full orchestrator pipeline
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="555-123-4567"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Select value={selectedState} onValueChange={setSelectedState} disabled={isSubmitting}>
                  <SelectTrigger id="state">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((st) => (
                      <SelectItem key={st} value={st}>{st}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="leadType">Lead Type</Label>
                <Select value={selectedLeadType} onValueChange={setSelectedLeadType} disabled={isSubmitting}>
                  <SelectTrigger id="leadType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_TYPE_OPTIONS.map((lt) => (
                      <SelectItem key={lt.id} value={lt.name}>{lt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting || !name || !phone}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Routing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Submit Lead
                  </>
                )}
              </Button>
              {result && (
                <Button variant="outline" onClick={handleReset}>
                  Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Result Display */}
        {result && (
          <Card className={`border-2 ${getOutcomeColor(result.routingOutcome)}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getOutcomeIcon(result.routingOutcome)}
                  <div>
                    <CardTitle className="text-lg">
                      {result.routingOutcome === 'ROUTED' ? 'Lead Routed Successfully' :
                       result.routingOutcome === 'RETRY_QUEUED' ? 'Lead Queued for Retry' :
                       result.routingOutcome === 'STOCKED' ? 'Lead Moved to Stock' :
                       'Lead Unsold'}
                    </CardTitle>
                    <CardDescription>
                      {result.intakeStatus}
                    </CardDescription>
                  </div>
                </div>
                <Badge 
                  variant={result.routingOutcome === 'ROUTED' ? 'default' : 
                           result.routingOutcome === 'RETRY_QUEUED' ? 'outline' : 'destructive'}
                  className="text-sm"
                >
                  {result.routingOutcome}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Lead ID */}
              <div className="flex items-center justify-between p-3 bg-background rounded-lg border">
                <span className="text-sm text-muted-foreground">Lead ID</span>
                <code className="text-sm font-mono">{result.leadId}</code>
              </div>
              
              {/* Routed Details */}
              {result.routingOutcome === 'ROUTED' && result.assignedAgentName && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-background rounded-lg border">
                    <span className="text-xs text-muted-foreground block">Assigned To</span>
                    <span className="font-medium">{result.assignedAgentName}</span>
                  </div>
                  <div className="p-3 bg-background rounded-lg border">
                    <span className="text-xs text-muted-foreground block">Charged Amount</span>
                    <span className="font-medium text-green-600">${result.chargedAmount}</span>
                  </div>
                </div>
              )}
              
              {/* Retry Details */}
              {result.routingOutcome === 'RETRY_QUEUED' && (
                <div className="p-3 bg-background rounded-lg border">
                  <span className="text-xs text-muted-foreground block">Retry Count</span>
                  <span className="font-medium">{result.retryCount} of 2 max</span>
                </div>
              )}
              
              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <span className="text-xs text-destructive font-medium block mb-1">Validation Errors</span>
                  <ul className="text-sm text-destructive space-y-1">
                    {result.errors.map((err, i) => (
                      <li key={i}>• {err.message}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Link to Inspector */}
              <div className="pt-2">
                <Button asChild variant="outline" className="w-full">
                  <Link to="/admin/routing-inspector">
                    <Eye className="h-4 w-4 mr-2" />
                    View in Routing Inspector
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Text */}
        {!result && (
          <Card className="border-dashed">
            <CardContent className="py-8">
              <div className="text-center text-muted-foreground">
                <Beaker className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  Submit a test lead to trigger the routing pipeline.
                </p>
                <p className="text-xs mt-1">
                  The lead will be persisted and visible in the Routing Inspector.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
